import React, { useMemo, useRef, useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { BASE_URL } from '../config';
import authStorage from '../utils/authStorage';

const BRAND = '#E53E14';

/**
 * Bulk employee invitation: upload, PREVIEW, then confirm.
 *
 * The preview step is the point of the component. The screen this replaces
 * posted the file straight to the server and sent every email immediately, so
 * the first anyone knew of forty bad addresses was forty bounces — and because
 * the old endpoint's duplicate check never actually ran, re-uploading a file
 * silently re-invited everybody. Nothing here sends until the summary has been
 * shown and confirmed.
 *
 * Used by BOTH roles and deliberately knows nothing about which:
 *   - a corporate admin renders it with neither `compId` nor `companyEmail`; the
 *     server pins the upload to their own company from the token and ignores
 *     anything the browser might claim.
 *   - a super-admin renders it from inside one company, naming that company.
 * The server re-derives and re-validates all of this, so the props below are a
 * request, never the authority: a corporate admin passing someone else's company
 * is simply ignored.
 */

/** Row verdicts, shared verbatim with the API. Renaming one side breaks both. */
const STATUS_META = {
    ok: { label: 'Will be invited', bg: '#ecfdf5', fg: '#047857' },
    already_member: { label: 'Already a member', bg: '#f3f4f6', fg: '#4b5563' },
    already_invited: { label: 'Already invited', bg: '#fffbeb', fg: '#b45309' },
    duplicate_in_file: { label: 'Duplicate in file', bg: '#fffbeb', fg: '#b45309' },
    invalid: { label: 'Needs fixing', bg: '#fef2f2', fg: '#b91c1c' },
};

const SUMMARY_ORDER = ['ok', 'already_member', 'already_invited', 'duplicate_in_file', 'invalid'];

const BulkInviteUpload = ({ compId, companyEmail, companyName, onDone, onCancel }) => {
    const [fileName, setFileName] = useState('');
    const [rows, setRows] = useState([]);
    const [preview, setPreview] = useState(null);
    const [result, setResult] = useState(null);
    const [resend, setResend] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [dragging, setDragging] = useState(false);
    const [showAll, setShowAll] = useState(false);
    const inputRef = useRef(null);

    const token = authStorage.getItem('auth-token');

    const summary = preview?.summary;
    const problemRows = useMemo(
        () => (preview?.rows || []).filter((r) => r.status !== 'ok'),
        [preview]
    );
    const visibleRows = useMemo(() => {
        const list = preview?.rows || [];
        // Problems first: with 500 rows the two that need attention must not be
        // somewhere in the middle of a scroll.
        const ordered = [...list].sort((a, b) => {
            const rank = (r) => (r.status === 'ok' ? 1 : 0);
            return rank(a) - rank(b) || a.rowNumber - b.rowNumber;
        });
        return showAll ? ordered : ordered.slice(0, 12);
    }, [preview, showAll]);

    const reset = () => {
        setFileName('');
        setRows([]);
        setPreview(null);
        setResult(null);
        setResend(false);
        setError('');
        setShowAll(false);
        if (inputRef.current) inputRef.current.value = '';
    };

    /**
     * Reads the spreadsheet in the browser and sends rows, not the file.
     *
     * SheetJS parses .xlsx and .csv alike, so the uploader does not have to care
     * which they exported, and the API stays a plain JSON endpoint with no
     * multipart handling or file storage to secure.
     */
    const readFile = (file) => {
        if (!file) return;
        setError('');
        setResult(null);
        setPreview(null);
        setFileName(file.name);

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const wb = XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
                const sheet = wb.Sheets[wb.SheetNames[0]];
                // defval keeps empty cells as empty strings, so a row missing an
                // email still arrives and can be reported as a numbered problem
                // rather than silently vanishing.
                const json = XLSX.utils.sheet_to_json(sheet, { defval: '' });
                if (!json.length) {
                    setError('That file has no rows in it.');
                    return;
                }
                setRows(json);
                validate(json);
            } catch (err) {
                setError('That file could not be read. Please upload a .xlsx or .csv file.');
            }
        };
        reader.onerror = () => setError('That file could not be read.');
        reader.readAsArrayBuffer(file);
    };

    const validate = async (parsed) => {
        setBusy(true);
        setError('');
        try {
            const res = await axios.post(
                `${BASE_URL}/user/invite/user/bulk/validate`,
                { rows: parsed, compId, companyEmail },
                { headers: { 'x-access-token': token } }
            );
            setPreview(res?.data?.data || null);
        } catch (err) {
            setError(err?.response?.data?.message || 'That file could not be checked. Please try again.');
            setFileName('');
            setRows([]);
        } finally {
            setBusy(false);
        }
    };

    const send = async () => {
        setBusy(true);
        setError('');
        try {
            const res = await axios.post(
                `${BASE_URL}/user/invite/user/bulk`,
                { rows, compId, companyEmail, resend },
                { headers: { 'x-access-token': token } }
            );
            setResult({ ...(res?.data?.data || {}), message: res?.data?.message });
            if (onDone) onDone();
        } catch (err) {
            setError(err?.response?.data?.message || 'The invitations could not be sent. Please try again.');
        } finally {
            setBusy(false);
        }
    };

    const downloadTemplate = () => {
        const sheet = XLSX.utils.aoa_to_sheet([
            ['firstName', 'lastName', 'email'],
            ['Ann', 'Lee', 'ann.lee@example.com'],
            ['Raj', 'Patel', 'raj.patel@example.com'],
        ]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, sheet, 'Employees');
        XLSX.writeFile(wb, 'employee-invite-template.xlsx');
    };

    /** Exports the rows that need attention, so they can be fixed and re-uploaded. */
    const downloadProblems = (list) => {
        const sheet = XLSX.utils.json_to_sheet(
            list.map((r) => ({
                Row: r.rowNumber,
                'First Name': r.firstName,
                'Last Name': r.lastName,
                Email: r.email,
                Problem: r.reason,
            }))
        );
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, sheet, 'Not invited');
        XLSX.writeFile(wb, 'invite-rows-to-check.xlsx');
    };

    // ---------------------------------------------------------------- result
    if (result) {
        const notSent = (result.rows || []).filter((r) => r.status !== 'ok');
        return (
            <div className="bg-white rounded-lg p-5 md:p-8">
                <div
                    className="rounded border border-solid p-4 text-sm"
                    style={{ borderColor: '#6ee7b7', backgroundColor: '#ecfdf5', color: '#047857' }}
                >
                    <span className="font-semibold">{result.message}</span>
                </div>

                {notSent.length > 0 && (
                    <div className="mt-5">
                        <p className="text-sm text-gray-600">
                            {notSent.length} row{notSent.length === 1 ? ' was' : 's were'} not
                            invited. Download the list to see why, correct it, and upload it again.
                        </p>
                        <button
                            type="button"
                            onClick={() => downloadProblems(notSent)}
                            className="mt-3 rounded-md border-0 appearance-none cursor-pointer px-4 py-2 text-sm font-medium text-gray-700"
                            style={{ backgroundColor: '#f3f4f6' }}
                        >
                            Download the {notSent.length} row{notSent.length === 1 ? '' : 's'} not invited
                        </button>
                    </div>
                )}

                <div className="mt-6 flex flex-wrap gap-3">
                    <button
                        type="button"
                        onClick={reset}
                        className="rounded-md border-0 appearance-none cursor-pointer px-4 py-2 text-sm font-medium text-white"
                        style={{ backgroundColor: BRAND }}
                    >
                        Upload another file
                    </button>
                    <button
                        type="button"
                        onClick={onCancel}
                        className="rounded-md border-0 appearance-none cursor-pointer px-4 py-2 text-sm font-medium text-gray-700"
                        style={{ backgroundColor: '#f3f4f6' }}
                    >
                        Done
                    </button>
                </div>
            </div>
        );
    }

    // --------------------------------------------------------------- preview
    return (
        <div className="bg-white rounded-lg p-5 md:p-8">
            <div className="mb-5">
                <h3 className="text-base font-semibold text-gray-900 normal-case">
                    Bulk invite employees
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                    {companyName
                        ? <>Employees will be invited to <span className="font-medium text-gray-700">{companyName}</span>. </>
                        : null}
                    Upload a .xlsx or .csv file with a <span className="font-medium">firstName</span>,{' '}
                    <span className="font-medium">lastName</span> and{' '}
                    <span className="font-medium">email</span> column. Nothing is sent until you have
                    seen what will happen and confirmed it.
                </p>
            </div>

            {error && (
                <div
                    className="mb-4 rounded border border-solid p-3 text-sm"
                    style={{ borderColor: '#fca5a5', backgroundColor: '#fef2f2', color: '#b91c1c' }}
                >
                    {error}
                </div>
            )}

            {!preview && (
                <div
                    onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                    onDragLeave={(e) => { e.preventDefault(); setDragging(false); }}
                    onDrop={(e) => {
                        e.preventDefault();
                        setDragging(false);
                        readFile(e.dataTransfer.files?.[0]);
                    }}
                    className="rounded-lg border-2 border-dashed p-8 text-center transition-colors"
                    style={{
                        borderColor: dragging ? BRAND : '#d1d5db',
                        backgroundColor: dragging ? '#fff7f5' : '#fafafa',
                    }}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill={dragging ? BRAND : '#9ca3af'}
                        className="mx-auto h-12 w-12"
                    >
                        <path fillRule="evenodd" d="M19.5 21a3 3 0 003-3V9a3 3 0 00-3-3h-5.379a.75.75 0 01-.53-.22L11.47 3.66A2.25 2.25 0 009.879 3H4.5a3 3 0 00-3 3v12a3 3 0 003 3h15zm-6.75-10.5a.75.75 0 00-1.5 0v4.19l-1.72-1.72a.75.75 0 00-1.06 1.06l3 3a.75.75 0 001.06 0l3-3a.75.75 0 10-1.06-1.06l-1.72 1.72V10.5z" clipRule="evenodd" />
                    </svg>

                    <p className="mt-3 text-sm font-medium text-gray-700">
                        {busy ? 'Checking the file…' : dragging ? 'Drop the file here' : 'Drag a file here'}
                    </p>
                    {fileName && !busy && (
                        <p className="mt-1 text-sm text-gray-500">{fileName}</p>
                    )}

                    <p className="my-3 text-xs font-semibold text-gray-400">OR</p>

                    <input
                        ref={inputRef}
                        id="bulk-invite-file"
                        type="file"
                        className="hidden"
                        accept=".xlsx,.xls,.csv"
                        onChange={(e) => readFile(e.target.files?.[0])}
                    />
                    <label
                        htmlFor="bulk-invite-file"
                        className="inline-block cursor-pointer rounded-md px-4 py-2 text-sm font-medium text-white"
                        style={{ backgroundColor: BRAND }}
                    >
                        Choose a file
                    </label>

                    <div className="mt-5">
                        <button
                            type="button"
                            onClick={downloadTemplate}
                            className="border-0 appearance-none bg-transparent cursor-pointer p-0 text-sm font-medium underline"
                            style={{ color: BRAND }}
                        >
                            Download the template
                        </button>
                    </div>
                </div>
            )}

            {preview && summary && (
                <>
                    <div className="flex flex-wrap gap-3">
                        {SUMMARY_ORDER.filter((k) => summary[k] > 0).map((k) => (
                            <div
                                key={k}
                                className="rounded-lg px-4 py-3"
                                style={{ backgroundColor: STATUS_META[k].bg }}
                            >
                                <div
                                    className="text-xl font-semibold"
                                    style={{ color: STATUS_META[k].fg }}
                                >
                                    {summary[k]}
                                </div>
                                <div className="text-xs font-medium" style={{ color: STATUS_META[k].fg }}>
                                    {STATUS_META[k].label}
                                </div>
                            </div>
                        ))}
                    </div>

                    <p className="mt-4 text-sm text-gray-600">
                        <span className="font-medium">{fileName}</span> — {summary.total} row
                        {summary.total === 1 ? '' : 's'} read.{' '}
                        {summary.ok > 0 ? (
                            <>
                                {summary.ok} invitation{summary.ok === 1 ? '' : 's'} will be sent,
                                valid for {preview.expiryDays} day
                                {preview.expiryDays === 1 ? '' : 's'}.
                            </>
                        ) : (
                            <>There is nothing to send from this file.</>
                        )}
                    </p>

                    {summary.already_invited > 0 && (
                        <label
                            className="mt-4 flex cursor-pointer items-start gap-3 rounded p-3"
                            style={{ backgroundColor: '#fffbeb' }}
                        >
                            <input
                                type="checkbox"
                                checked={resend}
                                onChange={(e) => setResend(e.target.checked)}
                                className="mt-0.5 cursor-pointer"
                            />
                            <span className="text-sm" style={{ color: '#92400e' }}>
                                <span className="font-medium">
                                    Also re-send to the {summary.already_invited} already invited.
                                </span>{' '}
                                They were invited before and have not signed up yet. Leave this
                                unticked and they are skipped — tick it to send them a fresh link,
                                which replaces their old one.
                            </span>
                        </label>
                    )}

                    <div className="mt-5 overflow-x-auto">
                        <table className="w-full border-collapse text-left text-sm" cellPadding={0} cellSpacing={0}>
                            <thead>
                                <tr className="text-xs uppercase text-gray-500" style={{ backgroundColor: '#FFF5F3' }}>
                                    <th scope="col" className="p-3 font-medium">Row</th>
                                    <th scope="col" className="p-3 font-medium">Name</th>
                                    <th scope="col" className="p-3 font-medium">Email</th>
                                    <th scope="col" className="p-3 font-medium">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleRows.map((r) => (
                                    <tr
                                        key={r.rowNumber}
                                        className="border-0 border-b border-solid border-gray-200 last:border-b-0"
                                    >
                                        <td className="p-3 text-gray-400">{r.rowNumber}</td>
                                        <td className="p-3 text-gray-900">
                                            {`${r.firstName} ${r.lastName}`.trim() || <span className="text-gray-400">—</span>}
                                        </td>
                                        <td className="max-w-[16rem] overflow-hidden text-ellipsis whitespace-nowrap p-3 text-gray-600">
                                            {r.email || <span className="text-gray-400">—</span>}
                                        </td>
                                        <td className="p-3">
                                            {/* The reason travels with the chip rather than being
                                                encoded by colour alone. */}
                                            <span
                                                className="inline-block rounded-full px-2 py-0.5 text-[11px] font-medium"
                                                style={{
                                                    backgroundColor: STATUS_META[r.status]?.bg,
                                                    color: STATUS_META[r.status]?.fg,
                                                }}
                                            >
                                                {STATUS_META[r.status]?.label || r.status}
                                            </span>
                                            {r.reason && (
                                                <span className="ml-2 text-xs text-gray-500">{r.reason}</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {(preview.rows || []).length > visibleRows.length && (
                        <button
                            type="button"
                            onClick={() => setShowAll(true)}
                            className="mt-3 border-0 appearance-none bg-transparent cursor-pointer p-0 text-sm font-medium underline"
                            style={{ color: BRAND }}
                        >
                            Show all {preview.rows.length} rows
                        </button>
                    )}

                    {problemRows.length > 0 && (
                        <div className="mt-3">
                            <button
                                type="button"
                                onClick={() => downloadProblems(problemRows)}
                                className="border-0 appearance-none bg-transparent cursor-pointer p-0 text-sm font-medium underline"
                                style={{ color: BRAND }}
                            >
                                Download the {problemRows.length} row
                                {problemRows.length === 1 ? '' : 's'} that will not be invited
                            </button>
                        </div>
                    )}

                    <div className="mt-6 flex flex-wrap gap-3">
                        <button
                            type="button"
                            disabled={busy || (summary.ok === 0 && !(resend && summary.already_invited > 0))}
                            onClick={send}
                            className={`rounded-md border-0 appearance-none px-5 py-2 text-sm font-medium text-white ${
                                busy || (summary.ok === 0 && !(resend && summary.already_invited > 0))
                                    ? 'cursor-default opacity-50'
                                    : 'cursor-pointer'
                            }`}
                            style={{ backgroundColor: BRAND }}
                        >
                            {busy
                                ? 'Sending…'
                                : `Send ${summary.ok + (resend ? summary.already_invited : 0)} invitation${
                                      summary.ok + (resend ? summary.already_invited : 0) === 1 ? '' : 's'
                                  }`}
                        </button>
                        <button
                            type="button"
                            onClick={reset}
                            disabled={busy}
                            className="rounded-md border-0 appearance-none cursor-pointer px-4 py-2 text-sm font-medium text-gray-700"
                            style={{ backgroundColor: '#f3f4f6' }}
                        >
                            Choose a different file
                        </button>
                        <button
                            type="button"
                            onClick={onCancel}
                            disabled={busy}
                            className="rounded-md border-0 appearance-none cursor-pointer bg-transparent px-4 py-2 text-sm font-medium text-gray-500"
                        >
                            Cancel
                        </button>
                    </div>
                </>
            )}

            {!preview && (
                <div className="mt-6">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="rounded-md border-0 appearance-none cursor-pointer px-4 py-2 text-sm font-medium text-gray-700"
                        style={{ backgroundColor: '#f3f4f6' }}
                    >
                        Cancel
                    </button>
                </div>
            )}
        </div>
    );
};

export default BulkInviteUpload;
